/**
 * Breadcrumb Component
 * Hierarchical navigation for detail pages with accessible markup.
 * Uses slash separators following Atlassian's breadcrumb pattern.
 * @packageDocumentation
 */

import { Link } from 'react-router';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A single breadcrumb item.
 */
export interface BreadcrumbItem {
  /** Display label for the breadcrumb */
  label: string;
  /** Navigation href (omit for current page) */
  href?: string;
  /** Optional icon to display before the label */
  icon?: ReactNode;
}

/**
 * Props for the Breadcrumb component.
 */
export interface BreadcrumbProps {
  /** Additional CSS classes */
  className?: string;
  /** Array of breadcrumb items from root to current page */
  items: BreadcrumbItem[];
}

/**
 * Breadcrumb navigation component for hierarchical page context.
 * Uses "/" separators matching Atlassian's breadcrumb convention.
 *
 * @param { BreadcrumbProps } props Component props.
 * @returns { JSX.Element } The breadcrumb navigation element.
 */
export const Breadcrumb = ({ items, className }: BreadcrumbProps) => (
  <nav aria-label="Breadcrumb" className={cn('mb-4', className)}>
    <ol className="flex items-center gap-1.5 text-sm">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <li key={item.href ?? item.label} className="flex items-center gap-1.5">
            {index > 0 && (
              <span className="select-none text-muted-foreground/50" aria-hidden="true">
                /
              </span>
            )}
            {isLast ? (
              <span className="font-medium text-foreground" aria-current="page">
                {item.icon && <span className="mr-1 inline-flex align-text-bottom">{item.icon}</span>}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.href ?? '#'}
                className="text-muted-foreground transition-colors duration-150 hover:text-foreground"
              >
                {item.icon && <span className="mr-1 inline-flex align-text-bottom">{item.icon}</span>}
                {item.label}
              </Link>
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);
